import React from 'react';
import { ConfirmDialog } from '../ui';

/** "3h 12m", "45m", "20s": how long the blocking run has been going, for the confirm to name. */
export function formatRunningFor(sinceIso?: string | null, now: number = Date.now()): string | null {
  if (!sinceIso) return null;
  const started = new Date(sinceIso).getTime();
  if (Number.isNaN(started)) return null;
  const totalSeconds = Math.floor(Math.max(0, now - started) / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${totalSeconds}s`;
}

interface ForceStartConfirmProps {
  open: boolean;
  /** The connector or brand being started. */
  targetName: string;
  /** What reads as running (it can be another brand's run), if known. */
  runningBrandName?: string;
  /** Start of the oldest live run (ISO); named in the message. */
  runningSince?: string | null;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirm for the admin "Force start" (MOT-656): the page refuses a new run
 * while something reads as running, and a run that really is live inside the
 * server's window is closed by a force start, so the confirm says how old it is.
 */
export const ForceStartConfirm: React.FC<ForceStartConfirmProps> = ({
  open,
  targetName,
  runningBrandName,
  runningSince,
  loading,
  onConfirm,
  onClose,
}) => {
  const age = formatRunningFor(runningSince);
  const who = runningBrandName ? `The ${runningBrandName} run` : 'The blocking run';
  return (
    <ConfirmDialog
      open={open}
      title={`Force start ${targetName}?`}
      message={
        <>
          {who} has been running for {age ?? 'an unknown time'}.
          {' '}A force start marks any run still shown as running for {targetName} as failed (forced by admin), records
          who did it in the audit log, and starts a new run now. It does not stop work that is genuinely still in
          progress, so use it when you believe the run is stuck.
        </>
      }
      confirmLabel="Force start"
      confirmColor="warning"
      loading={loading}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
};

export default ForceStartConfirm;
